/* eslint-disable indent */

import Vue from 'vue';
import { delay } from 'utils';
import { DefineOwnModule, RootActions, RootMutations } from 'stores/type';
import StorageManager, { StorageKey } from 'managers/storage.manager';
import { isElementExhausted, getElementsExhaustedState } from 'helpers/exhausted';
import { State, Getters, Actions, Mutations } from './types';

const fromStorage = StorageManager.load(StorageKey.WORKSPACE_INACTIVE);
const moduleState: State = {
  exhausted: {},
  visible: fromStorage !== null ? fromStorage.exhausted : [],
};

const storeModule: DefineOwnModule<
  State,
  Getters,
  Mutations & Pick<RootMutations, 'ANIMATION_EXHAUSTED_FINISH'>,
  Actions & Pick<RootActions, 'mixNew' | 'resetProgress'>
> = {
  state: moduleState,

  getters: {
    elementIsExhausted: (state) => (id) => {
      if (!state.exhausted.hasOwnProperty(id)) return null;

      return state.exhausted[id];
    },

    exhaustedVisible: (state) => state.visible,
    isExhaustedVisible: (_state, getters) => (id) => getters.exhaustedVisible.includes(id),
    hasExhaustedVisible: (_state, getters) => !!getters.exhaustedVisible.length,
  },

  actions: {
    exhaustedCheckIds({ commit, getters }, ids) {
      const states = getElementsExhaustedState(getters.elements, getters.historyMap, ids);
      commit('ELEMENTS_SET_EXHAUSTED', states);
    },

    async mixNew({ commit, getters }, { parents }) {
      // wait for mix parents to be added to history
      await delay(30);

      parents.forEach((id) => {
        const exhausted = isElementExhausted(getters.elements, getters.historyMap, id);
        if (!exhausted) return;

        commit('ELEMENT_SET_EXHAUSTED', { id, exhausted });
        commit('EXHAUSTED_VISIBLE_ADD', id);
      });
    },

    resetProgress({ commit, getters }) {
      const toReset = getters.elementsIds
        .filter((id) => getters.elementIsExhausted(id))
        .map((id) => ({ id, exhausted: false }));

      commit('ELEMENTS_SET_EXHAUSTED', toReset);
      commit('EXHAUSTED_VISIBLE_CLEAR');
    },
  },

  mutations: {
    ELEMENT_SET_EXHAUSTED(state, { id, exhausted }) {
      Vue.set(state.exhausted, id, exhausted);
    },
    ELEMENTS_SET_EXHAUSTED(state, list) {
      list.forEach(({ id, exhausted }) => Vue.set(state.exhausted, id, exhausted));
    },

    EXHAUSTED_VISIBLE_ADD(state, id) {
      if (state.visible.includes(id)) return;

      state.visible.push(id);
    },
    EXHAUSTED_VISIBLE_CLEAR(state) {
      state.visible = [];
    },
    ANIMATION_EXHAUSTED_FINISH(state, elementId) {
      state.visible = state.visible.filter((id) => id !== elementId);
    },
  },
};

export default storeModule;
